export interface NakamaSession {
  token: string;
  refresh_token: string;
  user_id: string;
  created: boolean;
}

export interface NakamaAccount {
  user: { id: string; username: string };
  email: string;
  wallet: string;
}

interface StorageReadRequest {
  collection: string;
  key: string;
  userId: string;
}

interface StorageWriteRequest {
  collection: string;
  key: string;
  value: Object;
  version?: string;
  permissionRead?: number;
  permissionWrite?: number;
}

interface StorageObjectAck {
  collection: string;
  key: string;
  version: string;
  userId: string;
}

interface StorageObject {
  collection: string;
  key: string;
  userId: string;
  value: string;
  version: string;
  permissionRead: number;
  permissionWrite: number;
}

interface NakamaClient {
  authenticateEmail(
    email: string,
    password: string,
    create: boolean,
    username: string | undefined,
  ): Promise<NakamaSession>;
  getAccount(session: NakamaSession): Promise<NakamaAccount>;
  readStorageObjects(
    session: NakamaSession,
    request: { objectIds: StorageReadRequest[] },
  ): Promise<{ objects: StorageObject[] }>;
  writeStorageObjects(
    session: NakamaSession,
    objects: StorageWriteRequest[],
  ): Promise<{ acks: StorageObjectAck[] }>;
}

interface NakamaGlobal {
  Client: new (
    serverKey: string,
    host: string,
    port: string,
    useSSL: boolean,
  ) => NakamaClient;
  Session: { restore(token: string, refreshToken: string): NakamaSession };
}

declare global {
  interface Window {
    nakamajs: NakamaGlobal | undefined;
  }
}

class NakamaService {
  private readonly client: NakamaClient | null;

  constructor() {
    const serverKey = "m4R7pW2tY9xL1cQ8vB5zN0gH3jF6kD4b";
    const host = "nakama-aethel.yoyooinc.com";
    const port = "7350";
    const useSSL = false; // Ajusta este valor según tu entorno mediante variables

    if (typeof window !== "undefined" && window.nakamajs) {
      this.client = new window.nakamajs.Client(serverKey, host, port, useSSL);
    } else {
      this.client = null;
    }
  }

  getClient(): NakamaClient {
    if (!this.client) {
      throw new Error(
        "El cliente de Nakama no está inicializado o el script no cargó a tiempo.",
      );
    }
    return this.client;
  }

  async authenticateEmail(
    email: string,
    password: string,
    create: boolean = false,
    username: string | undefined = undefined,
  ): Promise<NakamaSession> {
    const client = this.getClient();
    return await client.authenticateEmail(email, password, create, username);
  }

  async getAccount(session: NakamaSession): Promise<NakamaAccount> {
    const client = this.getClient();
    return await client.getAccount(session);
  }

  async readStorageObjects(
    session: NakamaSession,
    options: { objectIds: StorageReadRequest[] },
  ): Promise<{ objects: StorageObject[] }> {
    const client = this.getClient();
    return await client.readStorageObjects(session, options);
  }

  async writeStorageObjects(
    session: NakamaSession,
    objects: StorageWriteRequest[],
  ): Promise<{ acks: StorageObjectAck[] }> {
    const client = this.getClient();
    return await client.writeStorageObjects(session, objects);
  }

  restoreSession(token: string, refreshToken: string): NakamaSession {
    if (typeof window === "undefined" || !window.nakamajs) {
      throw new Error(
        "El SDK de Nakama no está disponible para restaurar la sesión.",
      );
    }
    return window.nakamajs.Session.restore(token, refreshToken);
  }
}

export const nakamaService = new NakamaService();
